import type { ReferenceServiceDataFragment, Settings } from "../../gql/graphql";
import type { DeepReadonly } from "../../util/type";
import { bound } from "ecce-react";


export type ConstantsData = DeepReadonly<Omit<ReferenceServiceDataFragment["constants"], "__typename">>;
export type ConstantsKey = keyof ConstantsData;
export type SettingsData = DeepReadonly<Omit<ReferenceServiceDataFragment["settings"], "__typename">>;
export type SettingsKey = keyof SettingsData;
export type CurrencyData = Readonly<Omit<ReferenceServiceDataFragment["currencies"], "__typename">[number]>;

export type ReferenceArea = DeepReadonly<{
	id: string;
	subAreas: { id: string }[];
}>;

export type ReferenceServiceConfig = {
	constants: ConstantsData;
	settings: SettingsData;
	currencies: readonly CurrencyData[];
	areas: readonly ReferenceArea[];
};

/**
 * Provides access to constants & settings.
 */
export class ReferenceService {
	readonly #constants: ConstantsData;
	readonly #settings: SettingsData;
	readonly currencies: readonly CurrencyData[];
	readonly areas: readonly ReferenceArea[];
	readonly subAreas: readonly string[];
	readonly #currenciesById: Record<string, CurrencyData>;
	readonly charterIndexCompanyId = "10759";

	constructor(config: ReferenceServiceConfig) {
		this.#constants = config.constants;
		this.#settings = config.settings;
		this.currencies = config.currencies;
		this.#currenciesById = Object.fromEntries(
			this.currencies.map(c => [ c.id, c ])
		);
		this.areas = config.areas;
		this.subAreas = config.areas.flatMap(area => area.subAreas).map(subArea => subArea.id);
	}

	@bound()
	constants(key: ConstantsKey): readonly string[] {
		return this.#constants[key];
	}

	@bound()
	setting<K extends SettingsKey>(key: K): Settings[K] {
		return this.#settings[key];
	}

	currency(id: string): CurrencyData {
		const c = this.#currenciesById[id];
		if(!c) {
			throw new Error("Unknown currency ID: " + id);
		}

		return c;
	}
}
